import React from "react";
import styles from "./style.module.scss";
import CheckIcon from "../../../../../../assets/svg/icons/common/CheckIcon";

/**
 * ApplicationStepper Component
 * Renders a stepper UI showing the progress through different sections of the application
 * 
 * @param {Object} props - Component props
 * @param {Array} props.sections - Array of section objects containing id, label, completed, and disabled status
 * @param {string} props.activeSection - ID of the currently active section
 * @param {Function} props.onSectionChange - Callback function when a section is clicked
 */
const ApplicationStepper = ({ sections, activeSection, onSectionChange, step }) => {
  const activeSectionIndex = sections.findIndex(section => section.id === activeSection);

  /**
   * Handles click events on section steps
   * @param {string} sectionId - ID of the clicked section
   * @param {boolean} isDisabled - Whether the section is disabled
   */
  const handleSectionClick = (sectionStep, isDisabled) => {
    if (isDisabled) return;

    if (onSectionChange && typeof onSectionChange === 'function') {
      onSectionChange(sectionStep);
    }
  };

  return (
    <div className={styles.stepper}>
      <div className={styles.stepsContainer}>
        {sections.map((section, index) => {
          const isActive = section.id === activeSection;
          const isCompleted = section.step < step;
          const isDisabled = section.disabled;
          const isBeforeActive = index < activeSectionIndex;
          const isLast = index === sections.length - 1;

          return (
            <div
              key={section.id}
              className={`${styles.step} ${isActive ? styles.active : ''} ${isCompleted ? styles.completed : ''} ${isDisabled ? styles.disabled : ''}`}
              onClick={() => handleSectionClick(section.step, isDisabled)}
              role="button"
              tabIndex={isDisabled ? -1 : 0}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleSectionClick(section.step, isDisabled);
                }
              }}
              aria-disabled={isDisabled}
            >
              <div className={styles.stepContent}>
                <div className={styles.graphContainer}>
                  <div className={styles.stepIndicator}>
                    {isCompleted ? (
                      <div className={styles.completedCircle}>
                        <CheckIcon width={16} height={16} />
                      </div>
                    ) : (
                      <div className={`${styles.stepCircle} ${isActive ? styles.activeCircle : ''}`} />
                    )}
                  </div>

                  {!isLast && (
                    <div
                      className={`${styles.connector} ${isBeforeActive || isCompleted ? styles.connectorCompleted : ''
                        }`}
                    />
                  )}
                </div>

                <div className={styles.textContainer}>
                  <div className={styles.stepLabel}>{section.label}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ApplicationStepper; 